/**
 * Install and update handling.
 *
 * A new version installs itself as soon as it is there: there is no decision
 * for the candidate to make, and a stale build that keeps talking to a moved
 * API is worse than a reload. The one exception is an exam in progress —
 * reloading mid-block is the last thing a candidate needs — so the update
 * waits for the block to end and applies itself then.
 */
import { useEffect, useState } from "react";
import { WifiOff } from "lucide-react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { useT } from "@/i18n";
import { isExamLocked, onExamLockChange } from "@/state/exam-lock";

export function AutoUpdate() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  const [examLocked, setLocked] = useState(isExamLocked);
  useEffect(() => onExamLockChange(setLocked), []);

  useEffect(() => {
    if (!needRefresh || examLocked) return;
    void updateServiceWorker(true);
  }, [needRefresh, examLocked, updateServiceWorker]);

  return null;
}

/**
 * The offline bar. With no network the app can neither save answers nor fetch
 * the next block, so it is better to say so plainly than to let the candidate
 * keep answering into the void.
 */
export function OfflineBanner() {
  const t = useT();
  // Online until the effect says otherwise: the prerendered page has no bar, and the first
  // render in the browser has to match it.
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    setOffline(!navigator.onLine);
    const online = (): void => setOffline(false);
    const gone = (): void => setOffline(true);
    window.addEventListener("online", online);
    window.addEventListener("offline", gone);
    return () => {
      window.removeEventListener("online", online);
      window.removeEventListener("offline", gone);
    };
  }, []);

  if (!offline) return null;

  return (
    <div
      role="alert"
      className="flex items-center justify-center gap-2 bg-warning/15 px-4 py-1.5 text-center text-xs font-medium text-foreground"
    >
      <WifiOff className="size-3.5 shrink-0" />
      {t("common.offline")}
    </div>
  );
}
